import React, { useEffect, useState } from 'react';
import { Button } from '@shared/components';
import { Plus, Minus } from 'lucide-react';
import { ServiceItem } from '../ServiceItem';
import { StartServiceSelect } from './StartServiceSelect';
import { ScheduleDateEditModal } from '../ScheduleDateEditModal/ScheduleDateEditModal';
import { DerivedSchedulePlaceholder } from './DerivedSchedulePlaceholder';
import type { ConfiguredService } from '../../../types/service.types';
import type {
    DerivedScheduleBounds,
    DerivedScheduleResponse,
    DerivedScheduleServiceEntry,
} from '../../../types/derivedSchedule.types';
import type { ServiceCountMode } from '../../../utils/resolveServiceCountMode';
import type { SetRowDateResult } from '../../../hooks/useScheduleOverrides';
import { resolveEditableDateFloor } from '../../../utils/scheduleRowBounds';
import type { PricedBasket } from '@features/quote/types/basketPricing.types';
import { pricedServiceLine } from '@features/quote/utils/pricedLines';
import { todayDateKey, type DateKey } from '@features/quote/utils/dateKey';

// The locked/guided render branch: the guided add/remove controls and the
// derived service list. Dumb by design — every value arrives from index.tsx.
interface DerivedServicesPanelProps {
    className?: string;
    mode: ServiceCountMode;
    isLoading: boolean;
    isDerivedLoading: boolean;
    isDerivedError: boolean;
    error: unknown;
    derivedSchedule: DerivedScheduleResponse | undefined;
    bounds: DerivedScheduleBounds | null;
    derivedRows: DerivedScheduleServiceEntry[];
    configuredDerivedRows: ConfiguredService[];
    derivedPricingResolved: boolean;
    canAddGuided: boolean;
    canRemoveGuided: boolean;
    handleGuidedAdd: () => void;
    handleGuidedRemove: () => void;
    currency: string;
    pricing?: PricedBasket;
    onStartIndexChange: (index: number) => void;
    setRowDate: (rowIndex: number, date: DateKey, cascade: boolean) => Promise<SetRowDateResult>;
    isMaterialising: boolean;
    materialiseError: string | null;
    clearMaterialiseError: () => void;
}

export const DerivedServicesPanel: React.FC<DerivedServicesPanelProps> = ({
    className,
    mode,
    isLoading,
    isDerivedLoading,
    isDerivedError,
    error,
    derivedSchedule,
    bounds,
    derivedRows,
    configuredDerivedRows,
    derivedPricingResolved,
    canAddGuided,
    canRemoveGuided,
    handleGuidedAdd,
    handleGuidedRemove,
    currency,
    pricing,
    onStartIndexChange,
    setRowDate,
    isMaterialising,
    materialiseError,
    clearMaterialiseError,
}) => {
    const [openRowIndex, setOpenRowIndex] = useState<number | null>(null);

    // A guided Remove can shrink the row list out from under an open modal;
    // drop the stale index so a later Add cannot re-open it unexpectedly.
    useEffect(() => {
        setOpenRowIndex((current) =>
            current !== null && current >= configuredDerivedRows.length ? null : current
        );
    }, [configuredDerivedRows.length]);

    const handleEditDate = (index: number): void => {
        clearMaterialiseError();
        setOpenRowIndex(index);
    };

    // Awaited so a rejection (a 422, or a mismatched emitted date) keeps the
    // modal open to show the error instead of closing over a discarded edit.
    const handleSubmitDate = async (date: DateKey, cascade: boolean): Promise<void> => {
        const submittedRowIndex = openRowIndex;
        if (submittedRowIndex === null) return;
        try {
            const result = await setRowDate(submittedRowIndex, date, cascade);
            if (result.status !== 'applied') return;
            setOpenRowIndex((current) => (current === submittedRowIndex ? null : current));
        } catch {
            /* materialiseError renders the reason; the modal stays open. */
        }
    };

    const startOptions = derivedSchedule?.startOptions ?? [];
    const effectiveStartIndex = derivedSchedule?.startIndex ?? null;
    const startedLaterThanAuto =
        effectiveStartIndex !== null &&
        startOptions.length > 0 &&
        effectiveStartIndex > startOptions[0].index;
    // Truncation on a non-looped plan is inherent, not a defect — named here
    // rather than left to an unexplained disabled Next.
    const truncationWarning =
        startedLaterThanAuto && bounds && derivedRows.length < bounds.min
            ? `This starting service leaves only ${derivedRows.length} service${derivedRows.length === 1 ? '' : 's'} in the schedule, fewer than the ${bounds.min} required. Choose an earlier starting service.`
            : null;

    return (
        <DerivedSchedulePlaceholder
            className={className}
            isLoading={isLoading}
            isDerivedLoading={isDerivedLoading}
            isDerivedError={isDerivedError}
            error={error}
            derivedSchedule={derivedSchedule}
            derivedRows={derivedRows}
            derivedPricingResolved={derivedPricingResolved}
        >
            <div className={`bg-mp-surface rounded-xl pt-4 ${className}`}>
                <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">
                    1. Configure Core Services
                </h2>
                {startOptions.length > 1 && (
                    <StartServiceSelect
                        options={startOptions}
                        value={derivedSchedule?.startIndex ?? null}
                        onChange={onStartIndexChange}
                        truncationWarning={truncationWarning}
                    />
                )}
                {mode === 'guided' && bounds && (
                    <div className="flex justify-end gap-2 mb-4">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleGuidedRemove}
                            disabled={!canRemoveGuided}
                            ariaDescribedBy={canRemoveGuided ? undefined : 'guided-remove-reason'}
                            startIcon={<Minus size={16} />}
                        >
                            Remove Service
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleGuidedAdd}
                            disabled={!canAddGuided}
                            ariaDescribedBy={canAddGuided ? undefined : 'guided-add-reason'}
                            startIcon={<Plus size={16} />}
                        >
                            Add Service
                        </Button>
                        {!canRemoveGuided && (
                            <span id="guided-remove-reason" className="sr-only">
                                Minimum {bounds.min} service{bounds.min === 1 ? '' : 's'} required
                            </span>
                        )}
                        {!canAddGuided && (
                            <span id="guided-add-reason" className="sr-only">
                                Maximum {bounds.max} service{bounds.max === 1 ? '' : 's'} reached
                            </span>
                        )}
                    </div>
                )}
                <div className="mt-2 space-y-4">
                    <h3 className="text-base font-semibold text-mp-foreground mb-2">Service Order</h3>
                    {configuredDerivedRows.map((service, index) => (
                        <ServiceItem
                            key={service.instanceId}
                            service={service}
                            onRemove={() => {}}
                            canRemove={false}
                            currency={currency}
                            pricedLine={pricedServiceLine(pricing, service.instanceId)}
                            onEditDate={() => handleEditDate(index)}
                        />
                    ))}
                </div>
                {openRowIndex !== null && configuredDerivedRows[openRowIndex] && (
                    <ScheduleDateEditModal
                        isOpen
                        serviceName={configuredDerivedRows[openRowIndex].name}
                        currentDate={configuredDerivedRows[openRowIndex].startDate}
                        minDate={resolveEditableDateFloor(
                            openRowIndex > 0
                                ? configuredDerivedRows[openRowIndex - 1].startDate
                                : undefined,
                            todayDateKey()
                        )}
                        uncascadedMaxDate={configuredDerivedRows[openRowIndex + 1]?.startDate}
                        isSubmitting={isMaterialising}
                        error={materialiseError}
                        onSubmit={(date, cascade) => {
                            void handleSubmitDate(date, cascade);
                        }}
                        onClose={() => setOpenRowIndex(null)}
                    />
                )}
            </div>
        </DerivedSchedulePlaceholder>
    );
};
